// Explicitly define production hydration mismatch behavior
window.__VUE_PROD_HYDRATION_MISMATCH_DETAILS__ = true;

import { createPinia } from "pinia";
import { createApp } from "vue";
import App from "./App.vue";
import "./assets/tailwind.css";
import { db } from "./firebase";
import router from "./router";
import { useAuthStore } from "./store/authStore"; // Import the auth store
import { useCurrencyStore } from "./store/currencyStore";
import "./utils/browserUtils"; // Import browser compatibility check
import { handleApiError } from "./utils/errorHandler";

// Create Vue app instance
const app = createApp(App);

// Initialize Pinia for state management
const pinia = createPinia();
app.use(pinia);

// Initialize stores
const authStore = useAuthStore();

// Initialize Auth Store and Currency Store BEFORE mounting the app
Promise.all([
  authStore.initAuth(),
  useCurrencyStore().initializeCurrency()
]).then(() => {
  // Log auth + email verification status (helps draftManager behavior)
  (async () => {
    try {
      const user = authStore.user;
      // Ensure we have the freshest verification status
      const emailVerified = user ? await authStore.checkEmailVerification() : false;
      console.info('[App] Authenticated:', !!user, 'email:', user?.email || null, 'emailVerified:', emailVerified);

      if (!user) {
        console.info('[Drafts] Remote saves will be skipped for unauthenticated users.');
      } else if (!emailVerified) {
        console.info('[Drafts] User email is not verified. If DraftManager is configured with requireVerifiedEmail=true, remote draft saves will be skipped.');
      } else {
        console.info('[Drafts] User is authenticated and email is verified. Remote draft saves are permitted (subject to rate-limiting).');
      }
    } catch (err) {
      console.error('[App] Error while checking authentication/verification status:', err);
    }

    // Use Vue Router
    app.use(router);

    // Mount the app only after auth and currency are initialized
    app.mount("#app");
  })();
}).catch(error => {
  console.error("Failed to initialize stores or router:", error);
  // Optionally, you could render a fallback UI here if initialization fails
  // For now, we'll just log the error.
});

// Global error handler with improved Firestore error handling
app.config.errorHandler = (error, vm, info) => {
  console.error("Global error:", error);
  console.error("Component:", vm);
  console.error("Error Info:", info);

  // Handle Firestore specific errors
  if (error.code && error.code.startsWith("firestore/")) {
    console.warn("Firestore error:", error.code);
  }

  return handleApiError(error);
};

// Global properties
app.config.globalProperties.$formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
  }).format(amount);
};

// Make Firebase services available to all components
app.config.globalProperties.$db = db;

// Mount the app - MOVED into initAuth().then() to ensure sequential initialization
// app.mount("#app");
